import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import type { ColumnsType } from "antd/es/table";
import API_CONFIG from "../../config/api";
import { useSelection } from "../../SelectionContext";
import { useAuth } from "../../context/AuthContext";

const { Text } = Typography;

type HotelProperty = {
  id: number;
  name: string;
  currency_id?: number | null;
};

type Currency = {
  id: number;
  code: string;
  symbol?: string | null;
  name: string;
};

type HotelGuest = {
  id: number;
  first_name: string;
  last_name: string;
};

type HotelReservation = {
  id: number;
  reservation_number: string;
  guest_first_name?: string | null;
  guest_last_name?: string | null;
  property_id: number;
};

type HotelFolio = {
  id: number;
  property_id: number;
  property_name?: string | null;
  reservation_id: number;
  reservation_number?: string | null;
  guest_id?: number | null;
  guest_first_name?: string | null;
  guest_last_name?: string | null;
  folio_number: string;
  status: string;
  currency_id: number;
  subtotal_amount: number | string;
  tax_amount: number | string;
  total_amount: number | string;
  paid_amount: number | string;
  balance_due: number | string;
  closed_at?: string | null;
  closed_by?: number | null;
};

type FolioItem = {
  id: number;
  item_type: string;
  description: string;
  quantity: number | string;
  unit_price: number | string;
  total_price: number | string;
  tax_id?: number | null;
  tax_amount?: number | string | null;
  service_id?: number | null;
  posted_at?: string | null;
};

type HotelService = {
  id: number;
  name: string;
  property_id: number;
};

type Tax = {
  id: number;
  name: string;
};

type FolioFormValues = {
  propertyId: number;
  reservationId: number;
  guestId?: number;
  folioNumber?: string;
  status: string;
  currencyId: number;
  subtotalAmount?: number;
  taxAmount?: number;
  totalAmount?: number;
  paidAmount?: number;
  balanceDue?: number;
  closedAt?: Dayjs;
  closedBy?: number;
};

type FolioItemFormValues = {
  itemType: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxId?: number;
  taxAmount?: number;
  serviceId?: number;
  postedAt?: Dayjs;
};

const statusLabels: Record<string, string> = {
  open: "Ouvert",
  closed: "Ferme",
  void: "Annule",
};

const statusColors: Record<string, string> = {
  open: "blue",
  closed: "green",
  void: "default",
};

const itemTypeLabels: Record<string, string> = {
  room: "Chambre",
  service: "Service",
  tax: "Taxe",
  adjustment: "Ajustement",
  deposit: "Acompte",
  refund: "Remboursement",
  other: "Autre",
};

const FoliosTab: React.FC = () => {
  const { selectedCompany } = useSelection();
  const { user, hasPermission } = useAuth();
  const [properties, setProperties] = useState<HotelProperty[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [guests, setGuests] = useState<HotelGuest[]>([]);
  const [reservations, setReservations] = useState<HotelReservation[]>([]);
  const [services, setServices] = useState<HotelService[]>([]);
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [folios, setFolios] = useState<HotelFolio[]>([]);
  const [loadingFolios, setLoadingFolios] = useState(false);
  const [filters, setFilters] = useState({
    propertyId: "all" as number | "all",
    status: "all" as string | "all",
    reservationId: "" as string,
    search: "",
  });
  const [isFolioModalOpen, setIsFolioModalOpen] = useState(false);
  const [editingFolio, setEditingFolio] = useState<HotelFolio | null>(null);
  const [selectedFolio, setSelectedFolio] = useState<HotelFolio | null>(null);
  const [folioItems, setFolioItems] = useState<FolioItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemSubmitting, setItemSubmitting] = useState(false);
  const [folioForm] = Form.useForm<FolioFormValues>();
  const [itemForm] = Form.useForm<FolioItemFormValues>();

  const requiresCompanySelection = Boolean(user?.is_superadmin && !selectedCompany);
  const canCreate = Boolean(hasPermission?.("Hotellerie.Folios.create"));
  const canEdit = Boolean(hasPermission?.("Hotellerie.Folios.edit"));
  const canDelete = Boolean(hasPermission?.("Hotellerie.Folios.delete"));

  const currencyMap = useMemo(() => {
    const map = new Map<number, Currency>();
    currencies.forEach((currency) => map.set(currency.id, currency));
    return map;
  }, [currencies]);

  const taxMap = useMemo(() => {
    const map = new Map<number, Tax>();
    taxes.forEach((tax) => map.set(tax.id, tax));
    return map;
  }, [taxes]);

  const serviceMap = useMemo(() => {
    const map = new Map<number, HotelService>();
    services.forEach((service) => map.set(service.id, service));
    return map;
  }, [services]);

  const formatMoney = useCallback(
    (value: number | string, currencyId?: number | null) => {
      const numeric = Number(value || 0);
      const formatted = new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(numeric);
      if (!currencyId) {
        return formatted;
      }
      const currency = currencyMap.get(currencyId);
      if (!currency) {
        return formatted;
      }
      return `${formatted} ${currency.symbol || currency.code}`;
    },
    [currencyMap]
  );

  const fetchProperties = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params = selectedCompany ? { company_id: selectedCompany } : {};
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/properties`, {
        params,
      });
      if (response.data.success) {
        setProperties(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching properties:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des proprietes"
      );
    }
  }, [requiresCompanySelection, selectedCompany]);

  const fetchCurrencies = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params: Record<string, string | number> = { page: 1, limit: 200 };
      if (selectedCompany) {
        params.companyId = selectedCompany;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/currencies`, {
        params,
      });
      setCurrencies(response.data.currencies || []);
    } catch (error: any) {
      console.error("[HOTEL] Error fetching currencies:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des devises"
      );
    }
  }, [requiresCompanySelection, selectedCompany]);

  const fetchGuests = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params = selectedCompany ? { company_id: selectedCompany } : {};
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/guests`, {
        params,
      });
      if (response.data.success) {
        setGuests(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching guests:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des clients"
      );
    }
  }, [requiresCompanySelection, selectedCompany]);

  const fetchReservations = useCallback(
    async (propertyId?: number) => {
      if (requiresCompanySelection) {
        return;
      }
      try {
        const params: Record<string, number> = {};
        if (selectedCompany) {
          params.company_id = selectedCompany;
        }
        if (propertyId) {
          params.property_id = propertyId;
        }
        const response = await axios.get(`${API_CONFIG.API_URL}/hotel/reservations`, {
          params,
        });
        if (response.data.success) {
          setReservations(response.data.data || []);
        }
      } catch (error: any) {
        console.error("[HOTEL] Error fetching reservations:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors du chargement des reservations"
        );
      }
    },
    [requiresCompanySelection, selectedCompany]
  );

  const fetchServices = useCallback(
    async (propertyId?: number) => {
      if (requiresCompanySelection) {
        return;
      }
      try {
        const params: Record<string, number> = {};
        if (selectedCompany) {
          params.company_id = selectedCompany;
        }
        if (propertyId) {
          params.property_id = propertyId;
        }
        const response = await axios.get(`${API_CONFIG.API_URL}/hotel/services`, {
          params,
        });
        if (response.data.success) {
          setServices(response.data.data || []);
        }
      } catch (error: any) {
        console.error("[HOTEL] Error fetching services:", error);
      }
    },
    [requiresCompanySelection, selectedCompany]
  );

  const fetchTaxes = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params: Record<string, string | number> = { status: "active", limit: 2000 };
      if (selectedCompany) {
        params.companyId = selectedCompany;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/taxes`, { params });
      setTaxes(response.data.taxes || []);
    } catch (error: any) {
      console.error("[HOTEL] Error fetching taxes:", error);
    }
  }, [requiresCompanySelection, selectedCompany]);

  const fetchFolios = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    setLoadingFolios(true);
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (filters.propertyId !== "all") {
        params.property_id = filters.propertyId;
      }
      if (filters.status !== "all") {
        params.status = filters.status;
      }
      if (filters.reservationId.trim()) {
        params.reservation_id = filters.reservationId.trim();
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/folios`, {
        params,
      });
      if (response.data.success) {
        setFolios(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching folios:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des folios"
      );
    } finally {
      setLoadingFolios(false);
    }
  }, [filters.propertyId, filters.reservationId, filters.status, requiresCompanySelection, selectedCompany]);

  const fetchFolioItems = useCallback(
    async (folioId: number) => {
      if (requiresCompanySelection) {
        return;
      }
      setItemsLoading(true);
      try {
        const params = selectedCompany ? { company_id: selectedCompany } : {};
        const response = await axios.get(
          `${API_CONFIG.API_URL}/hotel/folios/${folioId}/items`,
          { params }
        );
        if (response.data.success) {
          setFolioItems(response.data.data || []);
        }
      } catch (error: any) {
        console.error("[HOTEL] Error fetching folio items:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors du chargement des articles"
        );
      } finally {
        setItemsLoading(false);
      }
    },
    [requiresCompanySelection, selectedCompany]
  );

  useEffect(() => {
    fetchProperties();
    fetchCurrencies();
    fetchGuests();
    fetchTaxes();
  }, [fetchCurrencies, fetchGuests, fetchProperties, fetchTaxes]);

  useEffect(() => {
    fetchFolios();
  }, [fetchFolios]);

  const propertyIdForFolio = Form.useWatch("propertyId", folioForm);
  useEffect(() => {
    if (!isFolioModalOpen) {
      return;
    }
    if (propertyIdForFolio) {
      fetchReservations(propertyIdForFolio);
      fetchServices(propertyIdForFolio);
    }
  }, [fetchReservations, fetchServices, isFolioModalOpen, propertyIdForFolio]);

  const filteredFolios = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    if (!search) {
      return folios;
    }
    return folios.filter((folio) =>
      [folio.folio_number, folio.reservation_number]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search))
    );
  }, [filters.search, folios]);

  const openCreateFolio = () => {
    setEditingFolio(null);
    folioForm.resetFields();
    const defaultPropertyId =
      filters.propertyId !== "all" ? filters.propertyId : undefined;
    const propertyCurrency = defaultPropertyId
      ? properties.find((property) => property.id === defaultPropertyId)
          ?.currency_id
      : undefined;
    folioForm.setFieldsValue({
      propertyId: defaultPropertyId,
      currencyId: propertyCurrency || undefined,
      status: "open",
      subtotalAmount: 0,
      taxAmount: 0,
      totalAmount: 0,
      paidAmount: 0,
      balanceDue: 0,
    });
    setIsFolioModalOpen(true);
  };

  const openEditFolio = (folio: HotelFolio) => {
    setEditingFolio(folio);
    folioForm.setFieldsValue({
      propertyId: folio.property_id,
      reservationId: folio.reservation_id,
      guestId: folio.guest_id || undefined,
      folioNumber: folio.folio_number,
      status: folio.status,
      currencyId: folio.currency_id,
      subtotalAmount: Number(folio.subtotal_amount || 0),
      taxAmount: Number(folio.tax_amount || 0),
      totalAmount: Number(folio.total_amount || 0),
      paidAmount: Number(folio.paid_amount || 0),
      balanceDue: Number(folio.balance_due || 0),
      closedAt: folio.closed_at ? dayjs(folio.closed_at) : undefined,
      closedBy: folio.closed_by || undefined,
    });
    setIsFolioModalOpen(true);
  };

  const handleSaveFolio = async () => {
    const values = await folioForm.validateFields();
    if (!editingFolio && !canCreate) {
      message.warning("Vous n'avez pas les droits pour creer un folio.");
      return;
    }
    if (editingFolio && !canEdit) {
      message.warning("Vous n'avez pas les droits pour modifier ce folio.");
      return;
    }

    const payload = {
      ...(selectedCompany ? { company_id: selectedCompany } : {}),
      property_id: values.propertyId,
      reservation_id: values.reservationId,
      guest_id: values.guestId || null,
      folio_number: values.folioNumber || undefined,
      status: values.status,
      currency_id: values.currencyId,
      subtotal_amount: values.subtotalAmount ?? 0,
      tax_amount: values.taxAmount ?? 0,
      total_amount: values.totalAmount ?? 0,
      paid_amount: values.paidAmount ?? 0,
      balance_due: values.balanceDue ?? 0,
      closed_at: values.closedAt ? values.closedAt.format("YYYY-MM-DD HH:mm:ss") : null,
      closed_by: values.closedBy || null,
    };

    try {
      if (editingFolio) {
        const response = await axios.put(
          `${API_CONFIG.API_URL}/hotel/folios/${editingFolio.id}`,
          payload
        );
        if (response.data.success) {
          message.success("Folio mis a jour.");
        }
      } else {
        const response = await axios.post(`${API_CONFIG.API_URL}/hotel/folios`, payload);
        if (response.data.success) {
          message.success("Folio cree.");
        }
      }
      setIsFolioModalOpen(false);
      setEditingFolio(null);
      fetchFolios();
    } catch (error: any) {
      console.error("[HOTEL] Error saving folio:", error);
      message.error(
        error.response?.data?.message || "Erreur lors de la sauvegarde du folio"
      );
    }
  };

  const confirmDeleteFolio = (folio: HotelFolio) => {
    if (!canDelete) {
      message.warning("Vous n'avez pas les droits pour supprimer ce folio.");
      return;
    }
    Modal.confirm({
      title: "Supprimer le folio",
      content: `Confirmer la suppression du folio ${folio.folio_number} ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          const params = selectedCompany ? { company_id: selectedCompany } : {};
          const response = await axios.delete(
            `${API_CONFIG.API_URL}/hotel/folios/${folio.id}`,
            { params }
          );
          if (response.data.success) {
            message.success("Folio supprime.");
            fetchFolios();
          }
        } catch (error: any) {
          console.error("[HOTEL] Error deleting folio:", error);
          message.error(
            error.response?.data?.message ||
              "Erreur lors de la suppression du folio"
          );
        }
      },
    });
  };

  const openFolioDrawer = async (folio: HotelFolio) => {
    setSelectedFolio(folio);
    itemForm.resetFields();
    itemForm.setFieldsValue({
      itemType: "service",
      quantity: 1,
      unitPrice: 0,
      taxAmount: 0,
      postedAt: dayjs(),
    });
    fetchServices(folio.property_id);
    await fetchFolioItems(folio.id);
  };

  const handleAddItem = async () => {
    if (!selectedFolio) {
      return;
    }
    if (!canEdit) {
      message.warning("Vous n'avez pas les droits pour modifier ce folio.");
      return;
    }
    const values = await itemForm.validateFields();
    setItemSubmitting(true);
    try {
      const quantity = Number(values.quantity || 1);
      const unitPrice = Number(values.unitPrice || 0);
      const payload = {
        ...(selectedCompany ? { company_id: selectedCompany } : {}),
        item_type: values.itemType,
        description: values.description,
        quantity,
        unit_price: unitPrice,
        total_price: quantity * unitPrice,
        tax_id: values.taxId || null,
        tax_amount: values.taxAmount ?? 0,
        service_id: values.serviceId || null,
        posted_at: values.postedAt
          ? values.postedAt.format("YYYY-MM-DD HH:mm:ss")
          : null,
      };
      const response = await axios.post(
        `${API_CONFIG.API_URL}/hotel/folios/${selectedFolio.id}/items`,
        payload
      );
      if (response.data.success) {
        message.success("Article ajoute.");
        itemForm.resetFields();
        itemForm.setFieldsValue({
          itemType: "service",
          quantity: 1,
          unitPrice: 0,
          taxAmount: 0,
          postedAt: dayjs(),
        });
        fetchFolioItems(selectedFolio.id);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error adding folio item:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la creation de l'article"
      );
    } finally {
      setItemSubmitting(false);
    }
  };

  const confirmDeleteItem = (item: FolioItem) => {
    if (!selectedFolio) {
      return;
    }
    if (!canEdit) {
      message.warning("Vous n'avez pas les droits pour modifier ce folio.");
      return;
    }
    Modal.confirm({
      title: "Supprimer l'article",
      content: "Confirmer la suppression de cet article ?",
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          const params = selectedCompany ? { company_id: selectedCompany } : {};
          const response = await axios.delete(
            `${API_CONFIG.API_URL}/hotel/folios/${selectedFolio.id}/items/${item.id}`,
            { params }
          );
          if (response.data.success) {
            message.success("Article supprime.");
            fetchFolioItems(selectedFolio.id);
          }
        } catch (error: any) {
          console.error("[HOTEL] Error deleting folio item:", error);
          message.error(
            error.response?.data?.message ||
              "Erreur lors de la suppression de l'article"
          );
        }
      },
    });
  };

  const columns: ColumnsType<HotelFolio> = [
    {
      title: "Folio",
      key: "folio",
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.folio_number}</Text>
          <Text type="secondary">
            {record.property_name || `Propriete #${record.property_id}`}
          </Text>
        </Space>
      ),
    },
    {
      title: "Reservation",
      key: "reservation",
      render: (_value, record) => record.reservation_number || record.reservation_id,
    },
    {
      title: "Client",
      key: "guest",
      render: (_value, record) =>
        `${record.guest_first_name || ""} ${record.guest_last_name || ""}`.trim() ||
        (record.guest_id ? `Client #${record.guest_id}` : "-"),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (value: string) => (
        <Tag color={statusColors[value] || "default"}>
          {statusLabels[value] || value}
        </Tag>
      ),
    },
    {
      title: "Total",
      dataIndex: "total_amount",
      key: "total_amount",
      render: (value: number | string, record) =>
        formatMoney(value, record.currency_id),
    },
    {
      title: "Solde",
      dataIndex: "balance_due",
      key: "balance_due",
      render: (value: number | string, record) =>
        formatMoney(value, record.currency_id),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_value, record) => (
        <Space size={4}>
          <Button type="link" onClick={() => openFolioDrawer(record)}>
            Voir
          </Button>
          {canEdit ? (
            <Button type="link" onClick={() => openEditFolio(record)}>
              Modifier
            </Button>
          ) : null}
          {canDelete ? (
            <Button type="link" danger onClick={() => confirmDeleteFolio(record)}>
              Supprimer
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  const itemColumns: ColumnsType<FolioItem> = [
    {
      title: "Description",
      key: "description",
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.description}</Text>
          <Text type="secondary">
            {itemTypeLabels[record.item_type] || record.item_type}
          </Text>
        </Space>
      ),
    },
    {
      title: "Qte",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Unitaire",
      dataIndex: "unit_price",
      key: "unit_price",
      render: (value: number | string) =>
        selectedFolio ? formatMoney(value, selectedFolio.currency_id) : value,
    },
    {
      title: "Total",
      dataIndex: "total_price",
      key: "total_price",
      render: (value: number | string) =>
        selectedFolio ? formatMoney(value, selectedFolio.currency_id) : value,
    },
    {
      title: "Service",
      dataIndex: "service_id",
      key: "service_id",
      render: (value: number | null) =>
        value ? serviceMap.get(value)?.name || `Service #${value}` : "-",
    },
    {
      title: "Taxe",
      dataIndex: "tax_id",
      key: "tax_id",
      render: (value: number | null) =>
        value ? taxMap.get(value)?.name || `Taxe #${value}` : "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_value, record) =>
        canEdit ? (
          <Button type="link" danger onClick={() => confirmDeleteItem(record)}>
            Supprimer
          </Button>
        ) : null,
    },
  ];

  if (requiresCompanySelection) {
    return (
      <Card>
        <Alert
          message="Selection requise"
          description="Selectionnez une entreprise pour acceder aux folios."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <>
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Card
          title="Folios"
          extra={
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchFolios}
                loading={loadingFolios}
              >
                Rafraichir
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreateFolio}
                disabled={!canCreate}
              >
                Nouveau folio
              </Button>
            </Space>
          }
        >
          <Form layout="vertical">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={6}>
                <Form.Item label="Propriete">
                  <Select
                    value={filters.propertyId}
                    onChange={(value) =>
                      setFilters((prev) => ({ ...prev, propertyId: value }))
                    }
                    options={[
                      { value: "all", label: "Tous" },
                      ...properties.map((property) => ({
                        value: property.id,
                        label: property.name,
                      })),
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Statut">
                  <Select
                    value={filters.status}
                    onChange={(value) =>
                      setFilters((prev) => ({ ...prev, status: value }))
                    }
                    options={[
                      { value: "all", label: "Tous" },
                      { value: "open", label: statusLabels.open },
                      { value: "closed", label: statusLabels.closed },
                      { value: "void", label: statusLabels.void },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="ID reservation">
                  <Input
                    placeholder="ID reservation"
                    value={filters.reservationId}
                    onChange={(event) =>
                      setFilters((prev) => ({
                        ...prev,
                        reservationId: event.target.value,
                      }))
                    }
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Recherche">
                  <Input
                    placeholder="Numero folio"
                    value={filters.search}
                    onChange={(event) =>
                      setFilters((prev) => ({
                        ...prev,
                        search: event.target.value,
                      }))
                    }
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <Table
            columns={columns}
            dataSource={filteredFolios}
            rowKey="id"
            loading={loadingFolios}
            pagination={{ pageSize: 8 }}
          />
        </Card>
      </Space>

      <Modal
        title={editingFolio ? "Modifier folio" : "Nouveau folio"}
        open={isFolioModalOpen}
        onCancel={() => {
          setIsFolioModalOpen(false);
          setEditingFolio(null);
        }}
        onOk={handleSaveFolio}
        okText="Enregistrer"
      >
        <Form form={folioForm} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Propriete"
                name="propertyId"
                rules={[{ required: true, message: "Propriete requise" }]}
              >
                <Select
                  disabled={Boolean(editingFolio)}
                  options={properties.map((property) => ({
                    value: property.id,
                    label: property.name,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Reservation"
                name="reservationId"
                rules={[{ required: true, message: "Reservation requise" }]}
              >
                <Select
                  disabled={Boolean(editingFolio)}
                  showSearch
                  optionFilterProp="label"
                  options={reservations.map((reservation) => ({
                    value: reservation.id,
                    label: `${reservation.reservation_number} - ${
                      `${reservation.guest_first_name || ""} ${
                        reservation.guest_last_name || ""
                      }`.trim() || "Client"
                    }`,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Client" name="guestId">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={guests.map((guest) => ({
                    value: guest.id,
                    label: `${guest.first_name} ${guest.last_name}`,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Devise"
                name="currencyId"
                rules={[{ required: true, message: "Devise requise" }]}
              >
                <Select
                  disabled={Boolean(editingFolio)}
                  options={currencies.map((currency) => ({
                    value: currency.id,
                    label: `${currency.code} - ${currency.name}`,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Numero folio" name="folioNumber">
                <Input placeholder="Auto" disabled={Boolean(editingFolio)} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Statut" name="status">
                <Select
                  options={Object.keys(statusLabels).map((key) => ({
                    value: key,
                    label: statusLabels[key],
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Sous-total" name="subtotalAmount">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Montant taxe" name="taxAmount">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Total" name="totalAmount">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Paye" name="paidAmount">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Solde" name="balanceDue">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Ferme le" name="closedAt">
                <DatePicker showTime style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Ferme par" name="closedBy">
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title="Details folio"
        width={720}
        open={Boolean(selectedFolio)}
        onClose={() => setSelectedFolio(null)}
      >
        {selectedFolio ? (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Folio">
                {selectedFolio.folio_number}
              </Descriptions.Item>
              <Descriptions.Item label="Statut">
                {statusLabels[selectedFolio.status] || selectedFolio.status}
              </Descriptions.Item>
              <Descriptions.Item label="Reservation">
                {selectedFolio.reservation_number || selectedFolio.reservation_id}
              </Descriptions.Item>
              <Descriptions.Item label="Client">
                {`${selectedFolio.guest_first_name || ""} ${
                  selectedFolio.guest_last_name || ""
                }`.trim() || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Total">
                {formatMoney(selectedFolio.total_amount, selectedFolio.currency_id)}
              </Descriptions.Item>
              <Descriptions.Item label="Solde">
                {formatMoney(selectedFolio.balance_due, selectedFolio.currency_id)}
              </Descriptions.Item>
            </Descriptions>

            <Card
              title="Articles"
              extra={
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => fetchFolioItems(selectedFolio.id)}
                  loading={itemsLoading}
                >
                  Rafraichir
                </Button>
              }
            >
              {canEdit ? (
                <Form form={itemForm} layout="vertical">
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Type"
                        name="itemType"
                        rules={[{ required: true, message: "Type requis" }]}
                      >
                        <Select
                          options={Object.keys(itemTypeLabels).map((key) => ({
                            value: key,
                            label: itemTypeLabels[key],
                          }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Service" name="serviceId">
                        <Select
                          allowClear
                          options={services.map((service) => ({
                            value: service.id,
                            label: service.name,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item
                    label="Description"
                    name="description"
                    rules={[{ required: true, message: "Description requise" }]}
                  >
                    <Input />
                  </Form.Item>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item label="Quantite" name="quantity">
                        <InputNumber min={1} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label="Prix unitaire" name="unitPrice">
                        <InputNumber min={0} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label="Montant taxe" name="taxAmount">
                        <InputNumber min={0} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item label="Taxe" name="taxId">
                        <Select
                          allowClear
                          options={taxes.map((tax) => ({
                            value: tax.id,
                            label: tax.name,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Poste le" name="postedAt">
                        <DatePicker showTime style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Button
                    type="primary"
                    onClick={handleAddItem}
                    loading={itemSubmitting}
                  >
                    Ajouter article
                  </Button>
                </Form>
              ) : null}

              {folioItems.length === 0 && !itemsLoading ? (
                <Empty description="Aucun article" />
              ) : (
                <Table
                  columns={itemColumns}
                  dataSource={folioItems}
                  rowKey="id"
                  pagination={false}
                  loading={itemsLoading}
                  size="small"
                />
              )}
            </Card>
          </Space>
        ) : null}
      </Drawer>
    </>
  );
};

export default FoliosTab;
